// API Configuration and Functions for Scrawl Backend

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// CSRF token cache
let csrfToken = null;

/**
 * Get CSRF token from server
 */
const getCSRFToken = async () => {
  if (csrfToken) return csrfToken;
  
  try {
    const response = await fetch(`${API_BASE_URL}/csrf-token`, {
      credentials: 'include' // Include cookies
    });
    
    if (response.ok) {
      const data = await response.json();
      csrfToken = data.csrfToken;
      return csrfToken;
    }
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
  }
  
  return null;
};

/**
 * Get auth token from localStorage (fallback for compatibility)
 */
const getAuthToken = () => localStorage.getItem('scrawl_token');

/**
 * Create headers for API requests with auth and CSRF tokens
 */
const createHeaders = async (includeCSRF = true) => {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  // Add auth header if logged in (for backward compatibility)
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add CSRF token for state-changing requests
  if (includeCSRF) {
    const csrf = await getCSRFToken();
    if (csrf) {
      headers['x-csrf-token'] = csrf;
    }
  }
  
  return headers;
};

/**
 * Create a new paste
 */
export const createPaste = async (pasteData) => {
  const headers = await createHeaders(true); // Include CSRF token

  const response = await fetch(`${API_BASE_URL}/paste`, {
    method: 'POST',
    headers,
    credentials: 'include', // Include cookies
    body: JSON.stringify(pasteData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create paste');
  }

  return response.json();
};

/**
 * Update an existing paste (keep same URL)
 */
export const updatePaste = async (shortId, pasteData) => {
  const headers = await createHeaders(true); // Include CSRF token

  const response = await fetch(`${API_BASE_URL}/paste/${shortId}`, {
    method: 'PUT',
    headers,
    credentials: 'include', // Include cookies
    body: JSON.stringify(pasteData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update paste');
  }

  return response.json();
};

/**
 * Get a paste by short ID
 * @param {string} shortId - The paste's short ID
 * @param {boolean} isAuthor - If true, skip view count increment (author's first view)
 */
export const getPaste = async (shortId, isAuthor = false) => {
  const url = isAuthor 
    ? `${API_BASE_URL}/paste/${shortId}?skipView=true`
    : `${API_BASE_URL}/paste/${shortId}`;
    
  const response = await fetch(url, {
    credentials: 'include' // Include cookies
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to retrieve paste');
  }

  return response.json();
};

/**
 * Unlock a password-protected paste
 */
export const unlockPaste = async (shortId, passwordHash) => {
  const headers = await createHeaders(true); // Include CSRF token
  
  const response = await fetch(`${API_BASE_URL}/paste/${shortId}/unlock`, {
    method: 'POST',
    headers,
    credentials: 'include', // Include cookies
    body: JSON.stringify({ passwordHash }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unlock paste');
  }

  return response.json();
};

/**
 * Delete a paste
 */
export const deletePaste = async (shortId) => {
  const response = await fetch(`${API_BASE_URL}/paste/${shortId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete paste');
  }

  return response.json();
};

/**
 * Vote on a paste
 */
export const votePaste = async (shortId, type, action) => {
  const headers = await createHeaders(true); // Include CSRF token
  
  const response = await fetch(`${API_BASE_URL}/paste/${shortId}/vote`, {
    method: 'POST',
    headers,
    credentials: 'include', // Include cookies
    body: JSON.stringify({ type, action }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to vote');
  }

  return response.json();
};

/**
 * Check if API is available
 */
export const checkApiHealth = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      credentials: 'include' // Include cookies
    });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Check authentication status (using cookies)
 */
export const checkAuthStatus = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/status`, {
      credentials: 'include' // Include cookies
    });
    
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error('Failed to check auth status:', error);
  }
  
  return { isAuthenticated: false, user: null };
};

/**
 * Logout user (clear server-side cookie)
 */
export const logoutUser = async () => {
  try {
    const headers = await createHeaders(true); // Include CSRF token
    
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers,
      credentials: 'include'
    });
    
    return response.ok;
  } catch (error) {
    console.error('Logout failed:', error);
    return false;
  }
};;
